// Turns raw scores into coaching: what to fix first, what to do differently on
// the next take, and which exercise to do next. Rules only — every sentence is
// traceable to a number in the analysis.

import { soundInfo, ipaOf, respell, focusForSound } from './phonetics';

const clean = (w) => (w || '').toLowerCase().replace(/[^a-z']/g, '');

// The phoneme row for word i: same index when the lists line up, else by text.
const phonemesFor = (i, word, phonemeBreakdown) => {
  const same = phonemeBreakdown[i];
  if (same && clean(same.word) === clean(word)) return same;
  return phonemeBreakdown.find((p) => clean(p.word) === clean(word)) || null;
};

// Everything the UI shows about one word: how it is written in IPA, how to
// say it, and which of its sounds went wrong.
export const wordGuide = (word, i, phonemeBreakdown) => {
  const ph = phonemesFor(i, word.word, phonemeBreakdown);
  const codes = ph?.phonemes || [];
  const scores = ph?.phoneme_scores || [];
  let weakest = null;
  codes.forEach((code, k) => {
    const acc = scores[k]?.accuracy ?? 100;
    if (soundInfo(code) && (!weakest || acc < weakest.acc)) weakest = { code, acc, info: soundInfo(code) };
  });
  return {
    ipa: codes.length ? ipaOf(codes) : null,
    say: codes.length ? respell(codes) : null,
    weakest: weakest && weakest.acc < 80 ? weakest : null,
  };
};

const metricLabel = { pronunciation: 'pronunciation', accuracy: 'accuracy', fluency: 'flow', completeness: 'completeness', prosody: 'melody' };

export function sessionCoach(result) {
  const words = result.wordAnalysis || [];
  const pb = result.phonemeBreakdown || [];
  const s = result.scores;
  const wpm = result.prosody?.speakingRate || 0;

  // Words to fix first: lowest scores, each word once.
  // One card per word and per sound, so three cards teach three different things.
  const seenWords = new Set();
  const seenSounds = new Set();
  const fixFirst = [];
  words
    .map((w, i) => ({ w, i }))
    .filter(({ w }) => w.error_type === 'Omission' || w.score < 80)
    .sort((a, b) => a.w.score - b.w.score)
    .forEach(({ w, i }) => {
      if (fixFirst.length >= 3 || seenWords.has(clean(w.word))) return;
      const guide = wordGuide(w, i, pb);
      const sound = guide.weakest?.code;
      if (sound && seenSounds.has(sound)) return;
      seenWords.add(clean(w.word));
      if (sound) seenSounds.add(sound);
      fixFirst.push({ word: w.word, score: w.score, omitted: w.error_type === 'Omission', ...guide });
    });

  // The sound that went wrong most often in this take.
  const soundMisses = {};
  pb.forEach((item) => (item.phonemes || []).forEach((code, k) => {
    const acc = item.phoneme_scores?.[k]?.accuracy ?? 100;
    if (acc < 70 && soundInfo(code)) {
      soundMisses[code] = soundMisses[code] || { code, n: 0, words: new Set() };
      soundMisses[code].n += 1;
      soundMisses[code].words.add(clean(item.word));
    }
  }));
  const topSound = Object.values(soundMisses).sort((a, b) => b.n - a.n)[0] || null;

  const omitted = words.filter((w) => w.error_type === 'Omission').map((w) => w.word);
  const isLong = words.length > 20;

  const nextTake = [];
  if (topSound) {
    const inf = soundInfo(topSound.code);
    nextTake.push({
      title: `Fix your “${inf.say}” /${inf.ipa}/`,
      body: `It slipped in ${[...topSound.words].slice(0, 3).map((w) => `“${w}”`).join(', ')}. ${inf.tip}`,
      sound: topSound.code,
    });
  }
  if (omitted.length && s.completeness < 97) {
    nextTake.push({
      title: 'Say every word',
      body: `We didn’t hear ${omitted.slice(0, 3).map((w) => `“${w}”`).join(', ')}${omitted.length > 3 ? ` and ${omitted.length - 3} more` : ''}. Keep your eyes one word ahead of your voice.`,
    });
  }
  if (wpm > 170) {
    nextTake.push({ title: 'Slow down a little', body: `You spoke at ${Math.round(wpm)} words a minute. Aim for about 140 — slower speech sounds more confident, not less.` });
  } else if (isLong && wpm && wpm < 105) {
    nextTake.push({ title: 'Keep it flowing', body: `You were at ${Math.round(wpm)} words a minute. Read in phrases, not word by word — aim for about 130.` });
  }
  if (s.prosody && s.prosody < 75) {
    nextTake.push({ title: 'Make it sing', body: 'Lift the most important word in each sentence and let your voice fall at the full stop. Flat speech is the fastest thing to fix.' });
  }
  if (s.fluency && s.fluency < 75) {
    nextTake.push({ title: 'Fewer stops', body: 'Pause only at commas and full stops. Glue the small words to the next one: “out of” → “outta”.' });
  }
  if (!nextTake.length) {
    nextTake.push({ title: 'Raise the bar', body: result.overallScore >= 90 ? 'That was excellent. Try an Advanced passage, or read this one faster without losing clarity.' : 'Solid take. Do it once more and try to beat this score by five points.' });
  }

  // Which practice focus to recommend next.
  let focus = topSound ? focusForSound(topSound.code) : null;
  if (!focus) {
    const weakestMetric = ['prosody', 'fluency', 'completeness', 'accuracy']
      .filter((m) => s[m])
      .sort((a, b) => s[a] - s[b])[0];
    focus = { prosody: 'Intonation', fluency: 'Linking', completeness: 'Fluency', accuracy: 'Vowels' }[weakestMetric] || 'Rhythm';
  }

  // One-line verdict.
  const metricEntries = ['accuracy', 'fluency', 'prosody'].filter((m) => s[m]).map((m) => [m, s[m]]);
  const best = [...metricEntries].sort((a, b) => b[1] - a[1])[0];
  const worst = [...metricEntries].sort((a, b) => a[1] - b[1])[0];
  let verdict;
  if (result.overallScore >= 90) verdict = 'Clear, natural and easy to follow.';
  else if (topSound) verdict = `Your ${metricLabel[best[0]]} is strong. One sound is holding you back: “${soundInfo(topSound.code).say}” /${soundInfo(topSound.code).ipa}/.`;
  else if (worst && worst[1] < 80) verdict = `Your ${metricLabel[best[0]]} is strong — your ${metricLabel[worst[0]]} is where the next points are.`;
  else verdict = 'Good, even take. A few words are all that stand between you and 90.';

  return { verdict, fixFirst, nextTake: nextTake.slice(0, 3), focus };
}

// Pick exercises for a focus area, skipping the one just done.
export const recommend = (exercises, focus, { exclude, n = 2, preferLong = true } = {}) => {
  const pool = exercises.filter((e) => e.category === focus && e.id !== exclude);
  const ordered = preferLong ? [...pool.filter((e) => e.length === 'long'), ...pool.filter((e) => e.length !== 'long')] : pool;
  // stable but varied: rotate by day so the same user sees new picks over time
  const day = Math.floor(Date.now() / 86400000);
  const start = ordered.length ? day % ordered.length : 0;
  return [...ordered.slice(start), ...ordered.slice(0, start)].slice(0, n);
};

// Progress page: a short coach's note built from the insights.
export function progressCoach(progress) {
  const ins = progress.insights || {};
  const lines = [];
  const sessions = ins.sessions || progress.completed_exercises || 0;
  const avg = Math.round(progress.average_score || 0);
  lines.push(`${sessions} session${sessions === 1 ? '' : 's'} in, your average is ${avg}.`);
  if (ins.trend) {
    const d = ins.trend.delta;
    if (d >= 3) lines.push(`You’re improving: your last ${ins.trend.window} sessions average ${Math.round(ins.trend.recent)}, up ${Math.round(d)} points from before.`);
    else if (d <= -3) lines.push(`Your last ${ins.trend.window} sessions dipped ${Math.round(-d)} points — usually that means harder passages, not worse English.`);
    else lines.push('Your scores are steady. A new challenge — a harder passage — is how you break through.');
  }
  const m = ins.metrics || {};
  const named = Object.entries(m).filter(([k]) => k !== 'pronunciation' && k !== 'completeness');
  if (named.length >= 2) {
    const sorted = [...named].sort((a, b) => b[1] - a[1]);
    const [bk, bv] = sorted[0];
    const [wk, wv] = sorted[sorted.length - 1];
    if (bv - wv >= 5) lines.push(`Your ${metricLabel[bk]} (${Math.round(bv)}) is your strength; your ${metricLabel[wk]} (${Math.round(wv)}) is where the next points are.`);
  }
  const weak = ins.weak_sounds?.[0];
  if (weak && soundInfo(weak.phoneme)) {
    const inf = soundInfo(weak.phoneme);
    lines.push(`The sound to fix this week is “${inf.say}” /${inf.ipa}/ as in “${inf.ex}” — it averaged ${Math.round(weak.avg)} across ${weak.count} tries.`);
  }

  const focuses = [];
  (ins.weak_sounds || []).forEach((w) => { const f = focusForSound(w.phoneme); if (!focuses.includes(f)) focuses.push(f); });
  if (m.prosody && m.prosody < 80 && !focuses.includes('Intonation')) focuses.push('Intonation');
  if (m.fluency && m.fluency < 80 && !focuses.includes('Linking')) focuses.push('Linking');
  if (!focuses.length) focuses.push('Word stress', 'Fluency');
  return { lines, focuses: focuses.slice(0, 3) };
}
